import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, allVideos } from '@/mocks/data';

function formatNumber(n: number): string {
  if (n >= 10000) {
    return (n / 10000).toFixed(n >= 100000 ? 0 : 1) + '万';
  }
  return n.toString();
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">分类不存在</p>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryVideos = allVideos.filter((v) => v.category === slug);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: category.name },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `蜜桃视频 - ${category.name}分类`,
        description: category.description,
        url: `https://sweetvid.github.io/category/${slug}`,
      })}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Category Header */}
          <section className="relative w-full h-[200px] md:h-[280px] overflow-hidden">
            <img
              src={category.headerImage}
              alt={category.name}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/40"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm mb-4">
                <i className={`${category.icon} text-white text-2xl w-6 h-6 flex items-center justify-center`}></i>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {category.name}
              </h1>
              <p className="text-sm md:text-base text-white/70 max-w-xl">{category.description}</p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex items-center gap-2 overflow-x-auto flex-wrap pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Video Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14 md:pb-20">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-100 cursor-pointer border border-background-200"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs">
                      {video.duration}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-foreground-950 text-sm md:text-base line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors duration-200">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{video.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img
                          src={video.authorAvatar}
                          alt={video.author}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        <span className="text-xs text-foreground-500">{video.author}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                          {formatNumber(video.views)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-heart-line w-3.5 h-3.5 flex items-center justify-center"></i>
                          {formatNumber(video.likes)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {categoryVideos.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类下暂无内容</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}